import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import diamantService from '../../services/diamantService';
import walletService from '../../services/walletService';
import './DiamantStore.css';

// Initialize Stripe only if key is available
const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null;

// Main content component - receives stripe/elements as props
const DiamantStoreContent = ({ stripe, elements }) => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [dailyStatus, setDailyStatus] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [packagesRes, walletRes, dailyRes] = await Promise.all([
        diamantService.getPackages(),
        walletService.getWallet(),
        diamantService.getDailyStatus()
      ]);

      if (packagesRes.data?.success) {
        setPackages(packagesRes.data.data || []);
      }
      if (walletRes.data?.success) {
        setWallet(walletRes.data.data);
      }
      if (dailyRes.data?.success) {
        setDailyStatus(dailyRes.data.data);
      }
    } catch (err) {
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setError(null);
    setSuccess(null);
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements || !selectedPackage) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Create payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      // Initiate purchase
      const response = await diamantService.initiatePurchase(
        selectedPackage.id,
        paymentMethod.id
      );

      if (response.data?.success) {
        const { client_secret } = response.data.data || {};
        
        if (!client_secret) {
          setError('Erreur: client_secret manquant');
          setProcessing(false);
          return;
        }
        
        // Confirm payment
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          client_secret
        );

        if (confirmError) {
          setError(confirmError.message);
        } else if (paymentIntent?.status === 'succeeded') {
          // Confirm purchase on backend
          await diamantService.confirmPurchase(paymentIntent.id);
          
          const totalDiamonds = (selectedPackage.diamant_amount || 0) + (selectedPackage.bonus_diamant || 0);
          setSuccess(`Félicitations ! Vous avez acheté ${totalDiamonds} Diamants !`);

          fetchData(); // Refresh wallet
          setSelectedPackage(null);
          elements.getElement(CardElement)?.clear();
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'achat');
    } finally {
      setProcessing(false);
    }
  };

  const handleClaimDaily = async () => {
    try {
      const response = await diamantService.claimDailyReward();
      if (response.data?.success) {
        setSuccess(`Vous avez gagné ${response.data.data?.amount || 0} Diamants !`);

        fetchData();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la récupération du bonus');
    }
  };

  const formatPrice = (cents) => {
    if (!cents) return '0,00 €';
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  };

  // Safe number formatter
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="diamant-store loading">
        <div className="spinner"></div>
        <p>Chargement de la boutique...</p>
      </div>
    );
  }

  // Default wallet values
  const walletData = wallet || {};
  const balance = walletData.balance || 0;
  const isPremium = walletData.is_premium || false;

  return (
    <div className="diamant-store">
      <div className="store-header">
        <h1>💎 Boutique Diamants</h1>
        <p>Achetez des Diamants pour débloquer toutes les fonctionnalités premium</p>
      </div>

      {!stripePublicKey && (
        <div className="alert alert-warning">
          <span>⚠️</span> Configuration Stripe manquante. Veuillez configurer REACT_APP_STRIPE_PUBLIC_KEY dans votre fichier .env
        </div>
      )}

      <div className="current-balance-bar">
        <span>Solde actuel : <strong>{formatNumber(balance)} Diamants</strong></span>
        {isPremium && <span className="premium-tag">⭐ Premium</span>}
      </div>

      {error && (
        <div className="alert alert-error">
          <span>⚠️</span> {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>🎉</span> {success}
        </div>
      )}

      {/* Daily Reward Section */}
      {dailyStatus && (
        <div className="daily-reward-section">
          <div className="daily-reward-card">
            <div className="daily-icon">🎁</div>
            <div className="daily-info">
              <h3>Bonus quotidien</h3>
              <p>Connectez-vous chaque jour pour gagner des Diamants gratuits !</p>
              <div className="daily-streak">
                Série actuelle : <strong>{dailyStatus.current_streak || 0} jours</strong>
              </div>
            </div>
            <button 
              className={`btn-daily ${!dailyStatus.can_claim ? 'claimed' : ''}`}
              onClick={handleClaimDaily}
              disabled={!dailyStatus.can_claim}
            >
              {dailyStatus.can_claim 
                ? `Récupérer ${dailyStatus.reward_amount || 0} Diamants` 
                : 'Déjà récupéré aujourd\'hui'}
            </button>
          </div>
        </div>
      )}

      {/* Packages Grid */}
      <div className="packages-section">
        <h2>Choisissez votre pack</h2>
        <p className="packages-subtitle">Plus vous achetez, plus vous économisez !</p>
        
        {packages.length === 0 ? (
          <p className="no-packages">Aucun package disponible pour le moment.</p>
        ) : (
          <div className="packages-grid">
            {packages.map((pkg) => (
              <div 
                key={pkg.id}
                className={`package-card ${pkg.is_popular ? 'popular' : ''} ${pkg.is_best_value ? 'best-value' : ''} ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
                onClick={() => handlePackageSelect(pkg)}
              >
                {pkg.is_popular && <div className="badge popular-badge">Populaire</div>}
                {pkg.is_best_value && <div className="badge best-value-badge">Meilleur rapport</div>}
                
                <div className="package-header">
                  <h3>{pkg.name || 'Pack'}</h3>
                  <p className="package-description">{pkg.description || ''}</p>
                </div>
                
                <div className="package-diamant">
                  <span className="diamant-amount">{formatNumber(pkg.diamant_amount)}</span>
                  <span className="diamant-label">Diamants</span>
                  {(pkg.bonus_diamant || 0) > 0 && (
                    <span className="bonus-badge">+{pkg.bonus_diamant} bonus</span>
                  )}
                </div>
                
                <div className="package-price">
                  <span className="price">{formatPrice(pkg.price_cents)}</span>
                  {(pkg.bonus_diamant || 0) > 0 && (
                    <span className="price-per-diamant">
                      {((pkg.price_cents || 0) / 100 / ((pkg.diamant_amount || 0) + (pkg.bonus_diamant || 0))).toFixed(3).replace('.', ',')} €/Diamant
                    </span>
                  )}
                </div>
                
                <div className="package-benefits">
                  <div className="benefit">
                    <span className="benefit-icon">✓</span>
                    <span>Statut Premium activé</span>
                  </div>
                  <div className="benefit">
                    <span className="benefit-icon">✓</span>
                    <span>Accès aux vues de profil</span>
                  </div>
                  <div className="benefit">
                    <span className="benefit-icon">✓</span>
                    <span>Messages illimités</span>
                  </div>
                </div>
                
                <button className="btn-select" disabled={!stripePublicKey}>
                  {!stripePublicKey ? 'Indisponible' : selectedPackage?.id === pkg.id ? 'Sélectionné ✓' : 'Choisir'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment Form */}
      {selectedPackage && stripePublicKey && (
        <div className="payment-section">
          <h3>Paiement sécurisé</h3>
          <div className="payment-summary">
            <span>Pack sélectionné : <strong>{selectedPackage.name || 'Pack'}</strong></span>
            <span>Montant : <strong>{formatPrice(selectedPackage.price_cents)}</strong></span>
            <span>Diamants reçus : <strong>{formatNumber((selectedPackage.diamant_amount || 0) + (selectedPackage.bonus_diamant || 0))}</strong></span>
          </div>
          
          <form onSubmit={handlePurchase} className="payment-form">
            <div className="card-element-container">
              <label>Informations de carte</label>
              <div className="card-element">
                <CardElement 
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
            
            <button 
              type="submit" 
              className="btn-pay"
              disabled={!stripe || processing}
            >
              {processing ? 'Traitement...' : `Payer ${formatPrice(selectedPackage.price_cents)}`}
            </button>
            
            <p className="payment-security">
              🔒 Paiement sécurisé par Stripe. Vos informations bancaires ne sont pas stockées.
            </p>
          </form>
        </div>
      )}

      {/* Free Earnings Info */}
      <div className="free-earnings-section">
        <h3>💡 Comment gagner des Diamants gratuitement ?</h3>
        <div className="earnings-tips">
          <div className="tip">
            <span className="tip-icon">🎯</span>
            <div className="tip-content">
              <h4>Parrainez vos amis</h4>
              <p>Gagnez 25 Diamants pour chaque ami inscrit avec votre code</p>
            </div>
            <span className="tip-reward">+25 Diamants</span>
          </div>
          
          <div className="tip">
            <span className="tip-icon">📅</span>
            <div className="tip-content">
              <h4>Connexion quotidienne</h4>
              <p>Connectez-vous chaque jour pour cumuler des Diamants</p>
            </div>
            <span className="tip-reward">+2 Diamants/jour</span>
          </div>
          
          <div className="tip">
            <span className="tip-icon">❤️</span>
            <div className="tip-content">
              <h4>Loveshots populaires</h4>
              <p>Recevez des likes sur vos publications</p>
            </div>
            <span className="tip-reward">Jusqu'à +30 Diamants</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Inner component that uses Stripe hooks - must be inside Elements provider
const DiamantStoreWithStripe = () => {
  const stripe = useStripe();
  const elements = useElements();
  
  return <DiamantStoreContent stripe={stripe} elements={elements} />;
};

const DiamantStore = () => {
  // If no Stripe key, render without Stripe functionality
  if (!stripePublicKey) {
    return <DiamantStoreContent stripe={null} elements={null} />;
  }

  return (
    <Elements stripe={stripePromise}>
      <DiamantStoreWithStripe />
    </Elements>
  );
};

export default DiamantStore;
